<script>
import bem from '@/utils/bem';
import { mapActions, mapState } from 'vuex';

const b = bem('collection-cards-module');

export default {
  props: {
    moduleData: {
      type: Object,
      required: true,
    },
    isEditMode: {
      type: Boolean,
      default: true,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
    companyId: {
      type: [String, Number],
      default: null,
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    itemType: {
      type: String,
      default: 'collection',
    },
  },
  emits: ['update', 'select'],
  computed: {
    ...mapState({
      collections: (state) => state.collections.companyCollections,
      galleries: (state) => state.photoGallery.folders,
      currentCompany: (state) => state.companies.currentCompany,
    }),
    content() {
      if (this.moduleData?.parsedContent) {
        return this.moduleData.parsedContent;
      }
      if (!this.moduleData?.content) return {};
      if (typeof this.moduleData.content === 'string') {
        try {
          return JSON.parse(this.moduleData.content);
        } catch {
          return {};
        }
      }
      return this.moduleData.content;
    },
    contentType() {
      return this.content?.type || this.itemType;
    },
    isCollectionType() {
      return this.contentType === 'collection';
    },
    items() {
      if (this.isPopup && this.selectedIds.length > 0) {
        const source = this.isCollectionType ? this.collections : this.galleries;
        return source.filter((item) => this.selectedIds.includes(item.id));
      }

      const ids = this.isCollectionType
        ? this.content?.collectionIds
        : this.content?.galleryIds;

      if (!ids || !Array.isArray(ids)) return [];

      const source = this.isCollectionType ? this.collections : this.galleries;
      return source.filter((item) => ids.includes(item.id));
    },
  },
  watch: {
    companyId: {
      handler(newCompanyId) {
        if (newCompanyId) {
          if (this.isCollectionType) {
            this.getCompanyCollectionsAction(newCompanyId);
          } else {
            this.getFoldersAction(newCompanyId);
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    ...mapActions({
      getCompanyCollectionsAction: 'collections/getCompanyCollections',
      getFoldersAction: 'photoGallery/getFolders',
    }),
    getItemName(item) {
      return this.isCollectionType ? item.name : item.title;
    },
    getItemDescription(item) {
      const desc = item.description || '';
      if (!desc) return '';
      return desc.replace(/<[^>]*>/g, '');
    },
    getItemImage(item) {
      if (this.isCollectionType) {
        return item.previewImage || null;
      }
      if (item.coverImageUrl) return item.coverImageUrl;
      const images = item.images?.slice(0, 1) || [];
      return images.length > 0 ? images[0].imageUrl : null;
    },
  },
};
</script>

<template>
  <div :class="b({ mobile: isMobile, popup: isPopup, preview: !isPopup && !isEditMode })">
    <div :class="b('title', { mobile: isMobile, popup: isPopup })">
      {{ isCollectionType ? 'Select a Collection to Continue' : 'Select a Gallery to Continue' }}
    </div>
    <div v-if="items.length > 0" :class="b('grid', { mobile: isMobile, popup: isPopup })">
      <div
        v-for="item in items"
        :key="item.id"
        :class="b('card', { mobile: isMobile, popup: isPopup })"
      >
        <div :class="b('card-image', { mobile: isMobile, popup: isPopup })">
          <img
            v-if="getItemImage(item)"
            :src="getItemImage(item)"
            :alt="getItemName(item)"
          />
          <div v-else :class="b('card-placeholder')" />
        </div>
        <div :class="b('card-name', { mobile: isMobile, popup: isPopup })">
          {{ getItemName(item) }}
        </div>
        <div :class="b('card-desc', { mobile: isMobile, popup: isPopup })">
          {{ getItemDescription(item) }}
        </div>
      </div>
    </div>
    <div v-else :class="b('empty')">
      No items to display
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';
@import '@/styles/media';

.collection-cards-module {
  width: 1540px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  @include mediaMinMax(1025, $normal) {
    width: vw-full(1540);
    gap: vw-full(40);
  }

  &_popup {
    width: 1200px;
    gap: 24px;
    margin-bottom: 24px;

    @include mediaMinMax(1025, $normal) {
      width: vw-full(1200);
      gap: vw-full(24);
      margin-bottom: vw-full(24);
    }
  }

  &_preview {
    padding: 64px 0;

    @include mediaMinMax(1025, $normal) {
      padding: vw-full(64) 0;
    }
  }

  &_mobile {
    width: 100%;
    gap: 16px;
    padding: 40px 0;
  }

  &__title {
    font-family: Montserrat, sans-serif;
    color: #293241;
    text-align: center;
    font-size: 40px;
    font-weight: 600;
    line-height: normal;
    text-transform: capitalize;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(40);
    }

    &_popup {
      font-size: 28px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(28);
      }
    }

    &_mobile {
      font-size: 28px;
      font-weight: 500;
      padding: 0 28px;
    }
  }

  &__grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: center;
    gap: 32px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(32);
    }

    &_popup {
      gap: 24px;

      @include mediaMinMax(1025, $normal) {
        gap: vw-full(24);
      }
    }

    &_mobile {
      flex-direction: column;
      gap: 28px;
    }
  }

  &__card {
    border-radius: 18px 3px;
    background: #FFF;
    box-shadow: 4px 4px 8px 0 rgba(95, 138, 164, 0.20);
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 8px;

    @include mediaMinMax(1025, $normal) {
      gap: vw-full(16);
      border-radius: vw-full(18) vw-full(3);
      padding: vw-full(8);
    }

    &_popup {
      padding: 7px;
      gap: 11px;

      @include mediaMinMax(1025, $normal) {
        gap: vw-full(11);
        padding: vw-full(7);
      }
    }

    &_mobile {
      gap: 8px;
      padding: 4px;
      border-radius: 12px 3px;
      box-shadow: 3px 3px 6px 0 rgba(95, 138, 164, 0.20);
    }
  }

  &__card-image {
    width: 468px;
    height: 351px;
    border-radius: 12px 3px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    @include mediaMinMax(1025, $normal) {
      width: vw-full(468);
      height: vw-full(351);
      border-radius: vw-full(12) vw-full(3);
    }

    &_popup {
      width: 370px;
      height: 278px;

      @include mediaMinMax(1025, $normal) {
        width: vw-full(370);
        height: vw-full(278);
      }
    }

    &_mobile {
      width: 348px;
      height: 261px;
    }
  }

  &__card-placeholder {
    width: 100%;
    height: 100%;
    background: #f0f0f0;
  }

  &__card-name {
    width: 100%;
    font-family: Montserrat, sans-serif;
    color: #293241;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 26px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: uppercase;

    @include mediaMinMax(1025, $normal) {
      font-size: vw-full(26);
    }

    &_popup {
      font-size: 23px;

      @include mediaMinMax(1025, $normal) {
        font-size: vw-full(23);
      }
    }

    &_mobile {
      font-size: 20px;
    }
  }

  &__card-desc {
    max-width: 468px;
    font-family: Montserrat, sans-serif;
    font-size: 14px;
    color: #5e5d5d;
    text-align: center;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @include mediaMinMax(1025, $normal) {
      max-width: vw-full(468);
      font-size: vw-full(14);
    }
    &_popup {
      max-width: 370px;

      @include mediaMinMax(1025, $normal) {
        max-width: vw-full(370);
      }
    }
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 200px;
    font-family: Montserrat, sans-serif;
    font-size: 16px;
    color: #7f7f7f;

    @include mediaMinMax(1025, $normal) {
      height: vw-full(200);
      font-size: vw-full(16);
    }
  }
}
</style>
